import { useCallback, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { api } from '../lib/api'
import { groupNameProblem, type BranchGroup } from '../lib/branchNetwork'
import type { SetupStatusLine } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { EmptyState } from '../ui/parts'
import { Pager, usePaged } from '../ui/Pager'
import { Confirm, useSave } from './sectionKit'
import { Find, IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, RowArrow, toneOf } from './settingsKit'
import { Tick } from './setupKit'
import '../styles/org.css'
import '../styles/branchpage.css'

/**
 * Groups of branches: a name for a set of them, to tick them in one go.
 *
 * Said under the heading, because it is the thing people will assume otherwise: a
 * group holds no setup and gives nobody access. It is a label (org.BranchGroup),
 * and taking a branch out of a group changes nothing about the branch.
 *
 * The rows come from the Network section above it rather than from a request of
 * this list's own, so the member ticks and the list above can never disagree
 * about which branches exist. Every save announces SETUP_CHANGED through useSave,
 * and the section reloads both the groups and the rows on it.
 *
 * canShape is false for an administrator confined to named branches
 * (org.Staff.PinnedToBranches): the server refuses handleCreateBranchGroup,
 * handleUpdateBranchGroup and handleDeleteBranchGroup outright for them
 * (internal/api/branchgroups.go), because a group is organisation-wide and a
 * confined administrator naming one would be naming branches outside their own
 * boundary. They may still see which groups exist and who is in each — that is
 * what the Network list's own group line and the report's filter already show
 * them — so the same rows are drawn as plain rows that open nothing.
 */

export interface GroupDraft {
  id?: string
  name: string
  branchIds: string[]
}
type Draft = GroupDraft

/** The groups as a list card with its Add; a group's page belongs to the section (Network). */
export function BranchGroups({
  branches,
  groups,
  state,
  canShape,
  onOpen,
  onAdd,
}: {
  branches: SetupStatusLine[]
  groups: BranchGroup[]
  /** Where the section's first answer about groups stands. Until it comes an
   *  empty list means "not asked yet", not "there are none", and saying "No
   *  groups yet" for the second it takes is a sentence that is about to be
   *  wrong. After a refusal ('failed') the list says neither: the section's own
   *  error above it says what happened. */
  state: 'loading' | 'loaded' | 'failed'
  canShape: boolean
  onOpen: (group: BranchGroup) => void
  onAdd: () => void
}) {
  const { say } = useStaffText()
  const paged = usePaged(groups)

  const members = (g: BranchGroup) =>
    branches
      .filter((b) => g.branchIds.includes(b.branchId))
      .map((b) => b.branchName)
      .join(', ')

  return (
    <ListCard
      title={say('network.groups.eyebrow')}
      count={state === 'loaded' ? say('network.groupCount', { n: groups.length }) : undefined}
      action={
        canShape && (
          <Button variant="secondary" size="sm" isDisabled={state !== 'loaded'} onPress={onAdd}>
            <Icon name="plus" size={16} /> {say('org.grp.add')}
          </Button>
        )
      }
      foot={groups.length > 0 ? <Pager {...paged.pager} /> : undefined}
    >
      {state === 'loading' && <EmptyState title={say('common.loading')} />}
      {state === 'loaded' && groups.length === 0 && (
        <EmptyState icon={<Icon name="layers" size={20} />} title={say('network.groups.none')}>
          {say('org.grp.hint')}
        </EmptyState>
      )}
      {groups.length > 0 && (
        <ListTable
          label={say('network.groups.eyebrow')}
          cols={canShape ? 'minmax(0, 1fr) 120px 16px' : 'minmax(0, 1fr) 120px'}
          head={[say('network.groups.name'), say('network.col.branches'), ...(canShape ? [''] : [])]}
        >
          {paged.rows.map((g) => (
            <ListRow key={g.id} label={g.name} onOpen={canShape ? () => onOpen(g) : undefined}>
              <NameCell
                tile={<IconTile icon="layers" tone={toneOf(g.id)} size="sm" />}
                name={g.name}
                sub={members(g) || say('org.grp.noBranches')}
              />
              <span className="set-num">{say('network.groups.count', { n: g.branchIds.length })}</span>
              {canShape && <RowArrow />}
            </ListRow>
          ))}
        </ListTable>
      )}
    </ListCard>
  )
}

export function GroupDrawer({
  draft,
  groups,
  branches,
  organisation,
  onClose,
  flash,
}: {
  draft: Draft
  groups: BranchGroup[]
  branches: SetupStatusLine[]
  organisation?: string
  onClose: () => void
  flash: (text: string) => void
}) {
  const { say } = useStaffText()
  const [d, setD] = useState(draft)
  const isNew = !draft.id
  // Closing is enough: the section reloads the groups on SETUP_CHANGED, which
  // useSave announces.
  const { busy, error, save } = useSave(useCallback(() => onClose(), [onClose]), flash)
  // The name refused here in the same words the server would use, before a round
  // trip; the server still decides (GROUP_NAME_TAKEN). Said only once something
  // has been typed: an empty new group is not yet a mistake.
  const problem = groupNameProblem(d.name, groups, draft.id)
  const name = d.name.trim()
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const shownBranches = needle ? branches.filter((b) => b.branchName.toLowerCase().includes(needle)) : branches
  const toggle = (id: string) =>
    setD({ ...d, branchIds: d.branchIds.includes(id) ? d.branchIds.filter((x) => x !== id) : [...d.branchIds, id] })

  return (
    <InnerPage
      back={say('admin.section.network')}
      title={isNew ? say('network.groups.new') : draft.name}
      onClose={onClose}
      onSubmit={() =>
        void save(
          () =>
            isNew
              ? api.createBranchGroup({ name, branchIds: d.branchIds, ...(organisation ? { organisation } : {}) })
              : api.updateBranchGroup(draft.id!, { name, branchIds: d.branchIds }),
          say(isNew ? 'org.grp.added' : 'org.grp.saved', { name }),
        )
      }
      valid={!problem}
      busy={busy}
      submitLabel={say(isNew ? 'org.grp.add' : 'org.grp.save')}
      extra={
        isNew ? undefined : (
          // Asked before it is done, and said what it does not do: the branches
          // in the group are not touched.
          <Confirm
            ask={say('org.grp.delete')}
            question={say('org.grp.deleteAsk')}
            yes={say('common.delete')}
            keep={say('org.grp.keep')}
            busy={busy}
            onYes={() => void save(() => api.deleteBranchGroup(draft.id!), say('org.grp.deleted', { name: draft.name }))}
          />
        )
      }
    >
      {error && <Notice>{error}</Notice>}
      <label className="field">
        <span>{say('network.groups.name')}</span>
        <Input fullWidth value={d.name} aria-invalid={(problem !== null && d.name !== '') || undefined} onChange={(e) => setD({ ...d, name: e.target.value })} />
      </label>
      {problem && d.name !== '' && <p className="field-error">{say(problem)}</p>}
      {/* A list of ticks needs a fieldset and legend for each box to be named by
          its own branch and the set by the caption. */}
      <fieldset className="field">
        <legend>{say('network.groups.members')}</legend>
        {branches.length > 12 && <Find className="br-pickfind" value={q} onChange={setQ} label={say('org.br.find')} />}
        <div className="setup-ticks br-pickchecks">
          {shownBranches.map((b) => (
            <Tick key={b.branchId} checked={d.branchIds.includes(b.branchId)} onChange={() => toggle(b.branchId)}>
              {b.branchName}
              {!b.active && <small>{say('network.outOfService')}</small>}
            </Tick>
          ))}
        </div>
        <small>{say('network.groups.count', { n: d.branchIds.length })}</small>
      </fieldset>
    </InnerPage>
  )
}
